import React, { createRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import styles from './styles.scss';
import FocusedImage from 'Components/FocusedImage';
import Typography from 'Components/Typography';
import AssetModel from 'Stores/AssetModel';
import DealerContact from './dealerContact';
import Button from 'Components/Button';
import AccessibleAccordion from 'Components/AccessibleAccordion';
import { getWeekDayDe } from 'Utils/getDate';
import { MODAL_TYPES } from 'Containers/ContactForm/contactFormConstants';
import { withRouter } from 'react-router-dom';
import { withUrlParams } from 'Utils/withUrlParams';
import cn from 'classnames';

class LocationCard extends React.Component {
  constructor(props) {
    super(props);

    const { branch } = props;
    const image = branch && branch.aboutus && branch.aboutus.image;
    this.asset = createRef();

    if (image) {
      this.asset.current = new AssetModel({
        src: image.thumbnails['600x600'] || image.src,
        alternative: image.alternative,
      });
    }
  }

  getOpeningHours = (openingHoursArray, label, open) => {
    if (!openingHoursArray) return;

    const openingHours = openingHoursArray.filter((line) => 'Montag' !== line.dayOfWeekDe);

    return (
      <div
        className={cn(styles.openingHoursList, {
          [styles.openingHoursListOpen]: open,
        })}
      >
        <dl className={styles.openingHoursListDays}>
          {openingHours.map((hours, i) => (
            <Fragment key={`${hours.dayOfWeekDe}-${i}`}>
              <dt
                className={cn({
                  [styles.dayHighlight]: getWeekDayDe() === hours.dayOfWeekDe,
                })}
              >
                {hours && hours.dayOfWeekDe && (
                  <>
                    <span aria-hidden="true">{hours.dayOfWeekDe.substr(0, 2)}:</span>{' '}
                    <span className="sr-only">{hours.dayOfWeekDe}:</span>{' '}
                  </>
                )}
              </dt>
              <dd
                className={cn({
                  [styles.dayHighlight]: getWeekDayDe() === hours.dayOfWeekDe,
                })}
              >
                {`${hours.from} - ${hours.to}`} Uhr
              </dd>
            </Fragment>
          ))}
        </dl>
      </div>
    );
  };

  getOpeningHoursHeader = (openingHoursArray, label, open) => {
    if (!openingHoursArray) return;

    const dayHighlight = open && getWeekDayDe() === 'Montag';

    const openingHours = open
      ? openingHoursArray.filter((line) => 'Montag' === line.dayOfWeekDe)
      : openingHoursArray.filter((line) => getWeekDayDe() === line.dayOfWeekDe);

    return (
      <div
        className={cn(styles.openingHoursListHeader, {
          [styles.openingHoursListHeaderOpen]: open,
        })}
      >
        <h5 className={styles.openingHoursToggleName} aria-label={`Öffnungszeiten ${label}`}>
          {label}
        </h5>
        <div
          className={cn(styles.openingHoursToggleDays, {
            [styles.dayHighlight]: dayHighlight,
          })}
        >
          {open && (
            <>
              <span aria-hidden="true">Mo:</span>
              <span className="sr-only">Montag:</span>{' '}
            </>
          )}
          {!open && 'Heute:'}
        </div>
        <div
          className={cn(styles.openingHoursToggleHours, {
            [styles.dayHighlight]: dayHighlight,
          })}
        >
          {openingHours.map((hours, i) => (
            <div key={i}>{`${hours.from} - ${hours.to}`} Uhr</div>
          ))}
        </div>
      </div>
    );
  };

  setLeadsDealerId = () => {
    const { setSharedState, branch, dealer } = this.props;

    setSharedState('leadsMimicDealerId', branch.leadsMimicDealerId || dealer.dealerId);
  };

  renderHeadline = (isCurrentBranch, dealer, branch) => {
    const city = isCurrentBranch ? dealer.mainContact.city : branch.address?.city;

    return (
      <Typography.Headline
        level="h3"
        forceUppercase={true}
        value={`Standort ${city}`}
        ariaLabel={`Unser Standort ${city}`}
        className={isCurrentBranch ? styles.locationCard__changeLocationHeadline : 'sr-only'}
        additionalProps={{
          id: isCurrentBranch ? 'locationcard-mainbranch' : undefined,
          tabIndex: isCurrentBranch ? '-1' : undefined,
        }}
      />
    );
  };

  render() {
    const { branch, isCurrentBranch, outerControlToggle, openedHoursAccordions, dealer, e2e } =
      this.props;

    if (!branch.address && !branch.phone && !branch.email) {
      return null;
    }

    return (
      <article
        className={cn(styles.locationCard, {
          [styles.currentBranch]:
            dealer.branches.length > 1 && (!!isCurrentBranch || !branch.showLocationLink),
        })}
        data-e2e={e2e}
        id={`locationcard-${branch.mazdaId}`}
        aria-current={isCurrentBranch ? 'true' : 'false'}
        aria-label={`${isCurrentBranch ? 'aktueller ' : ''}Standort: ${branch.address?.city}`}
      >
        <header title="Autohaus-Standort">
          {this.asset.current && (
            <div aria-hidden="true" className={styles.locationCard__image}>
              <FocusedImage image={{ ...this.asset.current, alt: '' }} useThumb={false} />
            </div>
          )}
          <div
            className={cn(styles.locationCard__changeLocation, {
              [styles.currentBranch__changeLocation]: !!isCurrentBranch || !branch.showLocationLink,
            })}
          >
            {this.renderHeadline(isCurrentBranch, dealer, branch)}
            {!isCurrentBranch &&
              !branch.showLocationLink &&
              `Standort ${branch.address?.city ?? ''}`}
            {!isCurrentBranch && branch.showLocationLink && (
              <Button
                href={`https://${branch.domainName}`}
                target="_blank"
                label="Zum Standort wechseln"
                screenReaderLabel={`- die Website des Standorts in ${branch.address?.city} öffnet in einem neuen Tab oder Fenster`}
                disabled={isCurrentBranch}
                data-e2e={'goto_location'}
                resetLineHeight
                small
              />
            )}
          </div>
        </header>
        <div className={styles.locationCard__contact}>
          <DealerContact branch={branch} />
        </div>
        <div
          className={styles.bottomSection}
          data-some-accordion-open={openedHoursAccordions.length > 0}
        >
          <div className={styles.openingHours}>
            {((branch.isSalesPartner && branch.openingHoursForSales) ||
              (branch.isServicePartner && branch.openingHoursForServices)) && (
              <h4 className={styles.contact__headerHours}>Öffnungszeiten</h4>
            )}
            {branch.isSalesPartner && branch.openingHoursForSales && (
              <AccessibleAccordion
                classNames={{
                  root: styles.accordionInLocationRoot,
                  header: styles.accordionInLocationHeader,
                  label: styles.accordionInLocationLabel,
                  customTransiton:
                    !openedHoursAccordions.includes(`${branch.mazdaId}-sales`) &&
                    styles.closeTransition,
                }}
                label={this.getOpeningHoursHeader(
                  branch.openingHoursForSales,
                  'Verkauf:',
                  openedHoursAccordions.includes(`${branch.mazdaId}-sales`)
                )}
                outerControlToggle={outerControlToggle}
                accordionName={`${branch.mazdaId}-sales`}
                outerControlOpen={openedHoursAccordions.includes(`${branch.mazdaId}-sales`)}
                e2e={'sales'}
                small
              >
                {this.getOpeningHours(
                  branch.openingHoursForSales,
                  '',
                  openedHoursAccordions.includes(`${branch.mazdaId}-sales`)
                )}
              </AccessibleAccordion>
            )}

            {branch.isServicePartner && branch.openingHoursForServices && (
              <AccessibleAccordion
                classNames={{
                  root: styles.accordionInLocationRoot,
                  header: styles.accordionInLocationHeader,
                  label: styles.accordionInLocationLabel,
                  customTransiton:
                    !openedHoursAccordions.includes(`${branch.mazdaId}-service`) &&
                    styles.closeTransition,
                }}
                label={this.getOpeningHoursHeader(
                  branch.openingHoursForServices,
                  'Service:',
                  openedHoursAccordions.includes(`${branch.mazdaId}-service`)
                )}
                outerControlToggle={outerControlToggle}
                accordionName={`${branch.mazdaId}-service`}
                outerControlOpen={openedHoursAccordions.includes(`${branch.mazdaId}-service`)}
                e2e={'service'}
                small
              >
                {this.getOpeningHours(
                  branch.openingHoursForServices,
                  '',
                  openedHoursAccordions.includes(`${branch.mazdaId}-service`)
                )}
              </AccessibleAccordion>
            )}
          </div>
          <div className={styles.locationCard__contactButtons}>
            {' '}
            {(branch.isSalesPartner || branch.isServicePartner) && (
              <h4 className="sr-only">Termin am Standort {branch.address?.city} vereinbaren</h4>
            )}
            {branch.isSalesPartner && (
              <Button
                label="Beratungstermin"
                screenReaderLabel={`am Standort ${branch.address?.city} vereinbaren`}
                href={
                  isCurrentBranch || !!branch.leadsMimicDealerId
                    ? undefined
                    : `https://${branch.domainName}/?modaltype=REQUEST_FOR_CONSULTATION_FK&showmodal=true`
                }
                onClick={
                  isCurrentBranch || !!branch.leadsMimicDealerId
                    ? () => {
                        this.setLeadsDealerId();
                        this.props.updateMultipleUrlQuery({
                          modaltype: MODAL_TYPES.REQUEST_FOR_CONSULTATION_FK.name,
                          showmodal: 'true',
                        });
                      }
                    : undefined
                }
                data-e2e={'goto_beratungstermin'}
                resetLineHeight
                small
              />
            )}
            {branch.isServicePartner && (
              <Button
                label={'Werkstatttermin'}
                screenReaderLabel={`am Standort ${branch.address?.city} vereinbaren`}
                href={
                  isCurrentBranch || !!branch.leadsMimicDealerId
                    ? undefined
                    : `https://${branch.domainName}/?modaltype=ONLINE_SERVICE_BOOKING&showmodal=true`
                }
                onClick={
                  isCurrentBranch || !!branch.leadsMimicDealerId
                    ? () => {
                        this.setLeadsDealerId();
                        this.props.updateMultipleUrlQuery({
                          modaltype: MODAL_TYPES.ONLINE_SERVICE_BOOKING.name,
                          showmodal: 'true',
                        });
                      }
                    : undefined
                }
                data-e2e={'goto_werkstatttermin'}
                resetLineHeight
                small
              />
            )}
          </div>
        </div>
      </article>
    );
  }
}

LocationCard.propTypes = {
  imageSrc: PropTypes.string,
  branch: PropTypes.object,
  isCurrentBranch: PropTypes.bool,
  openedHoursAccordions: PropTypes.array,
  dealer: PropTypes.object,
};

export { LocationCard };

export default withRouter(withUrlParams(LocationCard));
